import {
  TranslationKey,
  useTranslate,
  UseTranslateType,
} from 'module/hooks/use-translation';
import {
  faCode,
  faH1,
  faH2,
  faH3,
  faListCheck,
  faListOl,
  faListUl,
  faQuoteLeft,
} from '@fortawesome/pro-regular-svg-icons';
import { Editor, Range } from '@tiptap/core';
import { snakeCase } from 'lodash';
import { ReactNode, useMemo } from 'react';
import { CommandPickerItem, CommandPickerLeafItem } from '../types';
import { CommandListItemIcon } from './common';
import { allowedOnlyInStartOfLine } from './utils';

enum FormattingCommand {
  HeadingLevelOne = 'heading-level-one',
  HeadingLevelTwo = 'heading-level-two',
  HeadingLevelThree = 'heading-level-three',
  BulletList = 'bullet-list',
  OrderedList = 'ordered-list',
  TaskList = 'task-list',
  Blockquote = 'blockquote',
  Code = 'code',
}
const formattingCommandMap: Record<
  FormattingCommand,
  {
    icon: ReactNode;
    execute: (editor: Editor) => void;
    isAllowed?: (editor: Editor, range: Range) => boolean;
  }
> = {
  [FormattingCommand.Blockquote]: {
    icon: <CommandListItemIcon icon={faQuoteLeft} />,
    execute: (editor: Editor) =>
      editor.chain().focus().toggleBlockquote().run(),
  },
  [FormattingCommand.Code]: {
    icon: <CommandListItemIcon icon={faCode} />,
    execute: (editor: Editor) => editor.chain().focus().toggleCode().run(),
  },
  [FormattingCommand.HeadingLevelOne]: {
    icon: <CommandListItemIcon icon={faH1} />,
    execute: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 1 }).run(),
    isAllowed: allowedOnlyInStartOfLine,
  },
  [FormattingCommand.HeadingLevelTwo]: {
    icon: <CommandListItemIcon icon={faH2} />,
    execute: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 2 }).run(),
    isAllowed: allowedOnlyInStartOfLine,
  },
  [FormattingCommand.HeadingLevelThree]: {
    icon: <CommandListItemIcon icon={faH3} />,
    execute: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 3 }).run(),
    isAllowed: allowedOnlyInStartOfLine,
  },
  [FormattingCommand.BulletList]: {
    icon: <CommandListItemIcon icon={faListUl} />,
    execute: (editor: Editor) =>
      editor.chain().focus().toggleBulletList().run(),
    isAllowed: allowedOnlyInStartOfLine,
  },
  [FormattingCommand.OrderedList]: {
    icon: <CommandListItemIcon icon={faListOl} />,
    execute: (editor: Editor) =>
      editor.chain().focus().toggleOrderedList().run(),
    isAllowed: allowedOnlyInStartOfLine,
  },
  [FormattingCommand.TaskList]: {
    icon: <CommandListItemIcon icon={faListCheck} />,
    execute: (editor: Editor) => editor.chain().focus().toggleTaskList().run(),
    isAllowed: allowedOnlyInStartOfLine,
  },
};
const formattingCommandAsItem = (
  command: FormattingCommand,
  translate: UseTranslateType,
): CommandPickerLeafItem => {
  return {
    id: command,
    label: translate(
      `editor.toolbar.basic_formatting.${snakeCase(command)}` as TranslationKey,
    ),
    icon: formattingCommandMap[command].icon,
    execute: formattingCommandMap[command].execute,
    isAllowed: formattingCommandMap[command].isAllowed,
  };
};

export const useGetDefaultFormattingCommands = (): CommandPickerItem[] => {
  const translate = useTranslate();

  return useMemo(
    () => [
      {
        id: 'formatting',
        label: translate('editor.commands.format.title'),
        items: Object.keys(formattingCommandMap).map((command) =>
          formattingCommandAsItem(command as FormattingCommand, translate),
        ),
      },
    ],
    [translate],
  );
};
